import { useEffect, useState } from 'react';
import { Download, Expand, LoaderCircle } from 'lucide-react';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import JSZip from 'jszip';

import { Button } from '@repo/ui/components/ui/button';
import { permissions as permifyPermissions } from '@repo/api/permify/constants';
import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { cn } from '@repo/ui/lib/utils';
import {
  SpotlightMedia,
  type SpotlightMediaReadyState,
} from '@repo/ui/components/custom';
import { NavigationControlsPosition } from './spotlight-media/navigation-controls/component';
import {
  type CandidateMedia,
  displayedMediaChangeType,
  spotlightMediaTypes,
  useCandidateSpotlightMedia,
} from '@repo/ui/hooks';
import { Lightbox } from './lightbox/component';
import { Submission } from '@repo/api/open-calls/graphql';

const getCandidatePreviewImages = (
  candidateMedia: CandidateMedia[],
  currentMediaIndex: number,
  spotlightMedia?: CandidateMedia,
) => {
  if (spotlightMedia) {
    return [
      ...candidateMedia.filter((media) => media.id > spotlightMedia.id),
      ...candidateMedia.filter((media) => media.id < spotlightMedia.id),
    ];
  }

  return candidateMedia.filter((media) => media.id !== currentMediaIndex);
};
export const CandidatePortfolio = ({
  candidate,
  spotlightMedia,
  spotlightMediaClassName,
  spotlightMediaNavigationControlsPosition,
  mediaAPIURL,
}: {
  candidate: Submission;
  spotlightMedia?: CandidateMedia;
  spotlightMediaClassName?: string;
  spotlightMediaNavigationControlsPosition?: NavigationControlsPosition;
  mediaAPIURL: string;
}) => {
  const {
    currentMediaIndex,
    candidateMedia,
    currentMedia,
    setCurrentMedia,
    nextMedia,
    handleDisplayedMediaChange,
  } = useCandidateSpotlightMedia({
    initialMedia: spotlightMedia,
    photos: candidate.data.portfolio.photos,
    videos: candidate.data.portfolio.videos,
  });
  const hasMultipleMedia = candidateMedia.length > 1;
  const [showPortfolioLightbox, setShowPortfolioLightbox] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const [portfolioSpotlightMediaState, setPortfolioSpotlightMediaState] =
    useState<SpotlightMediaReadyState>();
  const [lightboxSpotlightMediaState, setLightboxSpotlightMediaState] =
    useState<SpotlightMediaReadyState>();
  const [candidatePreviewImages, setCandidatePreviewImages] = useState<
    CandidateMedia[]
  >(
    getCandidatePreviewImages(
      candidateMedia,
      currentMediaIndex,
      spotlightMedia,
    ),
  );

  const handleArrowLeftClick = () => {
    handleMediaChange(displayedMediaChangeType.previous);
  };

  const handleArrowRightClick = () => {
    handleMediaChange(displayedMediaChangeType.next);
  };

  const handleMediaChange = (
    mediaChangeType: string,
    userSelectedMediaIndex?: number,
  ) => {
    const spotlightMediaState = showPortfolioLightbox
      ? lightboxSpotlightMediaState
      : portfolioSpotlightMediaState;
    const { newMediaId } = handleDisplayedMediaChange({
      mediaChangeType,
      onMediaCleanUp: (mediaType) => {
        if (mediaType === spotlightMediaTypes.video) {
          setIsVideoMuted(true);
          spotlightMediaState?.disposeVideo?.();
        }

        if (mediaType === spotlightMediaTypes.image) {
          spotlightMediaState?.startImageMediaAnimation?.();
        }
      },
      userSelectedMediaIndex,
    });

    const newMediaIndex = candidateMedia.findIndex(
      (media) => media.id === newMediaId,
    );

    if (newMediaIndex !== -1 && candidateMedia.length > 1) {
      const nextIndex = (newMediaIndex + 1) % candidateMedia.length;
      const mediaAfterSelected = candidateMedia.slice(nextIndex);
      const mediaBeforeSelected = candidateMedia.slice(0, nextIndex);

      const allPreviewMedia = [
        ...mediaAfterSelected,
        ...mediaBeforeSelected,
      ].filter((media) => media.id !== newMediaId);

      setCandidatePreviewImages(allPreviewMedia);
    }
  };

  const handlePreviewImageClick = (clickedMedia: CandidateMedia) => {
    handleMediaChange(displayedMediaChangeType.next, clickedMedia.id);
  };

  const handleVolumeButtonClick = () => {
    setIsVideoMuted(!isVideoMuted);
  };

  const handlePortfolioLightboxOpenChange = (open: boolean) => {
    setShowPortfolioLightbox(open);
  };

  const handleSpotlightMediaExpandClick = () => {
    portfolioSpotlightMediaState?.pauseVideo?.();
    setShowPortfolioLightbox(true);
  };

  const fetchImageBlob = async (url: string = '') => {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Unable to download portfolio');
    }

    return await response.blob();
  };

  const downloadPortolio = async () => {
    try {
      const zip = new JSZip();
      const candidateImages = await Promise.all(
        candidateMedia
          .filter(
            (media) =>
              media.type === spotlightMediaTypes.image && !media.isProcessing,
          )
          .map(async (media, index) => {
            const targetUrl = new URL(media.url);
            const url = `${mediaAPIURL}?format=${targetUrl.searchParams.get('format')}&url=${encodeURIComponent(targetUrl.searchParams.get('url') || '')}`;

            const mediaName = media.metadata?.type ?? `photo ${index + 1}`;

            return {
              blob: await fetchImageBlob(url),
              name: `${mediaName}.${media.metadata?.extension}`,
            };
          }),
      );

      candidateImages.forEach((image) => {
        zip.file(image.name, image.blob, { binary: true });
      });

      const zipped = await zip.generateAsync({ type: 'blob' });

      saveAs(
        zipped,
        `${candidate.profile.firstName} ${candidate.profile.lastName}.zip`.toLowerCase(),
      );
    } catch (error) {
      showErrorToast({
        title: (error as unknown as Error).message,
        description: 'Please wait a moment and try again.',
      });

      return Promise.reject(error);
    }
  };

  const handlePortfolioDownloadClick = async () => {
    toast.promise(downloadPortolio(), {
      loading: (
        <p className="font-normal text-sm flex flex-row items-center">
          <LoaderCircle className="size-4 mr-4 animate-spin" />
          Downloading portfolio
        </p>
      ),
      success: <p className="font-normal text-sm">Portfolio downloaded</p>,
      className: 'pl-6 pr-8 py-6',
    });
  };

  useEffect(() => {
    setCandidatePreviewImages(
      getCandidatePreviewImages(
        candidateMedia,
        currentMediaIndex,
        spotlightMedia,
      ),
    );
  }, [candidate]);

  useEffect(() => {
    return () => {
      portfolioSpotlightMediaState?.disposeVideo?.();
      lightboxSpotlightMediaState?.disposeVideo?.();
    };
  }, []);

  return (
    <>
      <section className="py-2 px-4 hidden lg:flex bg-white flex-row justify-between items-center h-13">
        <span className="text-lg font-semibold">Portfolio</span>

        <section className="flex flex-row items-center">
          <Button
            className="rounded-full w-8 bg-transparent hover:bg-transparent text-cn-foreground p-2.5 border-0 shadow-none hidden lg:flex"
            variant="outline"
            data-permission={permifyPermissions.openCalls.exportCandidates}
            onClick={handlePortfolioDownloadClick}
          >
            <Download className="size-4" />
          </Button>

          {currentMedia.isProcessing && (
            <span className="flex items-center justify-center size-8">
              <LoaderCircle className="size-4 animate-spin" />
            </span>
          )}
          {!currentMedia.isProcessing &&
            spotlightMediaNavigationControlsPosition ===
              NavigationControlsPosition.center && (
              <Button
                className="rounded-full w-8 bg-transparent hover:bg-transparent text-cn-foreground p-2.5 border-0 shadow-none"
                variant="outline"
                role="open-lightbox-button"
                onClick={() => setShowPortfolioLightbox(true)}
              >
                <Expand className="size-4" />
              </Button>
            )}
        </section>
      </section>
      <Lightbox
        open={showPortfolioLightbox}
        currentMedia={currentMedia}
        nextMedia={nextMedia}
        previewImages={candidatePreviewImages}
        imageMediaAlt={`Candidate ${candidate?.profile?.firstName} ${candidate?.profile?.lastName}`}
        isVideoMuted={isVideoMuted}
        videoAutoplay={showPortfolioLightbox}
        onSpotlightMediaReady={setLightboxSpotlightMediaState}
        onOpenChange={handlePortfolioLightboxOpenChange}
        onMediaSwipeRight={handleArrowRightClick}
        onMediaSwipeLeft={handleArrowLeftClick}
        onVolumeButtonClick={handleVolumeButtonClick}
        onImageAnimationEnd={() => setCurrentMedia(nextMedia)}
        onPreviewImageClick={handlePreviewImageClick}
        onArrowLeftClick={handleArrowLeftClick}
        onArrowRightClick={handleArrowRightClick}
        ariaDescription="Candidate Porfolio Lightbox"
        showNavigationControls={hasMultipleMedia}
      />
      <span className="h-full w-full">
        <section
          className={cn(
            'relative h-full w-full xl:h-[80%] cursor-zoom-in',
            spotlightMediaClassName,
          )}
        >
          <SpotlightMedia
            media={currentMedia}
            nextMedia={nextMedia}
            imageMediaAlt={`Candidate ${candidate?.profile?.firstName} ${candidate?.profile?.lastName}`}
            isVideoMuted={isVideoMuted}
            videoAutoplay={!showPortfolioLightbox}
            showPlayPauseVideoControl={true}
            onMediaReady={setPortfolioSpotlightMediaState}
            onArrowLeftClick={handleArrowLeftClick}
            onArrowRightClick={handleArrowRightClick}
            onExpandClick={handleSpotlightMediaExpandClick}
            onMediaSwipeLeft={handleArrowLeftClick}
            onMediaSwipeRight={handleArrowRightClick}
            onVolumeButtonClick={handleVolumeButtonClick}
            navigationControlsPosition={
              spotlightMediaNavigationControlsPosition
            }
            showNavigationControls={hasMultipleMedia}
            onImageAnimationEnd={() => setCurrentMedia(nextMedia)}
            onMediaDoubleClick={() => setShowPortfolioLightbox(true)}
          />
        </section>
        <section
          className="flex flex-row h-[20%] gap-2 py-2"
          data-testid="media-thumbnails"
        >
          {candidatePreviewImages.slice(0, 4).map((media, i) => (
            <img
              key={i}
              onClick={() => handlePreviewImageClick(media)}
              className="w-[calc((100%/4)-6px)] h-full object-contain cursor-pointer"
              src={media.thumbnailUrl}
              alt={`Candidate ${candidate?.profile?.firstName} ${candidate?.profile?.lastName}`}
            />
          ))}
        </section>
      </span>
    </>
  );
};
